import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Globe, ShieldCheck, Sprout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IMG } from "@/lib/mockData";

const VALUES = [
  { icon: Heart, title: "Authenticity first", text: "Real heritage recipes from the people who grew up cooking them — never a franchise imitation." },
  { icon: ShieldCheck, title: "Verified & trusted", text: "Every chef and restaurant is personally verified for quality, hygiene and craft." },
  { icon: Globe, title: "The world on a plate", text: "40+ cuisines and counting, celebrating the diversity of global food culture." },
  { icon: Sprout, title: "Good for the planet", text: "Local sourcing, eco packaging and greener delivery baked into everything we do." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative min-h-[60vh] flex items-center grain">
        <img src={IMG.gourmet1} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#f5a623]">Our story</span>
            <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter text-white leading-[0.95] mt-4">About Jollanda.</h1>
            <p className="mt-5 font-display italic text-2xl text-[#ff5a5a]">Lekker with you.</p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-24">
        <p className="font-display text-2xl sm:text-3xl leading-relaxed text-neutral-800">
          Jollanda began with a simple frustration: the most delicious, authentic food is often cooked by people
          without a storefront — the aunty making the best jollof in the neighbourhood, the nonna rolling pasta at dawn.
          We built Jollanda to give those independent chefs a stage, and to give eaters a direct line to the real thing.
        </p>
        <p className="text-neutral-600 leading-relaxed mt-6">
          Today we connect thousands of food lovers with verified indie chefs and restaurants across dozens of cuisines.
          Every order supports a small kitchen and celebrates a culinary tradition. That's what "Lekker with you" means to
          us — great food, shared, in good company.
        </p>
      </section>

      <section className="border-y border-neutral-200 bg-white py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">What we stand for</p>
          <h2 className="font-display text-4xl sm:text-5xl font-black text-neutral-900 mb-12">Our values.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="border border-neutral-200 p-8 bg-[#fbf9f7]" data-testid={`value-${i}`}>
                <v.icon className="w-8 h-8 text-[#d00000] mb-5" />
                <h3 className="font-display text-xl font-bold text-neutral-900">{v.title}</h3>
                <p className="text-neutral-600 mt-3 leading-relaxed text-sm">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 text-center">
        <h2 className="font-display text-4xl font-black text-neutral-900">Hungry to join the table?</h2>
        <div className="flex gap-3 justify-center mt-8">
          <Link to="/marketplace"><Button className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none h-12 px-8">Browse meals</Button></Link>
          <Link to="/register"><Button variant="ghost" className="border border-neutral-300 text-neutral-900 rounded-none h-12 px-8">Become a chef</Button></Link>
        </div>
      </section>
    </div>
  );
}
